import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../../pages/dashboard/LoadingSpinner';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Sector,
} from 'recharts';

const RADIAN = Math.PI / 180;

const renderCustomLabel = (props) => {
  const {
    cx, cy, midAngle, outerRadius, percent, index, data,
  } = props;

  const radius = outerRadius * 1.15;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const lineX = cx + (outerRadius + 10) * Math.cos(-midAngle * RADIAN);
  const lineY = cy + (outerRadius + 10) * Math.sin(-midAngle * RADIAN);
  const isRight = x > cx;
  const entry = data[index];

  if (percent < 0.005) return null;

  return (
    <g>
      <path d={`M${lineX},${lineY}L${x},${y}`} stroke="#334155" strokeWidth={1.2} fill="none" />
      <circle cx={x} cy={y} r={3} fill="#334155" />
      <rect
        x={x + (isRight ? 6 : -146)}
        y={y - 20}
        width={140}
        height={34}
        fill="white"
        rx={4}
        opacity={0.95}
      />
      <text
        x={x + (isRight ? 12 : -12)}
        y={y - 6}
        textAnchor={isRight ? 'start' : 'end'}
        fontSize={12}
        fontWeight={600}
        fill="#1e293b"
      >
        {entry.type}
      </text>
      <text
        x={x + (isRight ? 12 : -12)}
        y={y + 8}
        textAnchor={isRight ? 'start' : 'end'}
        fontSize={11}
        fill="#64748b"
      >
        {`${entry.count} (${(percent * 100).toFixed(1)}%)`}
      </text>
    </g>
  );
};

const renderActiveShape = ({
  cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
}) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 15}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      opacity={0.3}
    />
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  </g>
);

const CustomTooltip = ({ active, payload, total }) => {
  if (active && payload && payload.length) {
    const entry = payload[0].payload;
    const percentage = ((entry.count / total) * 100).toFixed(1);
    return (
      <div className="bg-white p-3 border border-gray-200 rounded shadow-md">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="font-semibold text-gray-800">{entry.type}</span>
        </div>
        <p className="text-sm text-gray-600 m-0">Count: <strong>{entry.count}</strong></p>
        <p className="text-sm text-gray-600 m-0">Percentage: <strong>{percentage}%</strong></p>
      </div>
    );
  }
  return null;
};

const Piechart = ({ data, title }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  const total = data.reduce((sum, entry) => sum + entry.count, 0);

  useEffect(() => {
    if (data && data.length > 0) {
      setIsLoading(true);
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 500); // simulate loading/render time
      return () => clearTimeout(timeout);
    }
  }, [data]);

  return (
    <div
      className="card p-4 shadow-sm border border-gray-200 bg-white rounded-xl h-100"
      style={{ margin: '0 auto' }}
    >
      <h5 className="mb-4 text-lg font-semibold text-gray-800">{title}</h5>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "250px" }}>
          <LoadingSpinner title={`Rendering chart...`} />
        </div>
      ) : (
        <ResponsiveContainer>
          <PieChart>
            <defs>
              {data.map((entry, index) => (
                <linearGradient id={`grad-${index}`} key={index} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                  <stop offset="100%" stopColor={entry.color} stopOpacity={0.5} />
                </linearGradient>
              ))}
            </defs>

            <Pie
              data={data}
              cx="50%"
              cy="50%"
              outerRadius={140}
              innerRadius={60}
              labelLine={false}
              label={(props) => renderCustomLabel({ ...props, data })}
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              dataKey="count"
              nameKey="type"
              minAngle={1}
              isAnimationActive
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={`url(#grad-${index})`}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip total={total} />} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default Piechart;
